import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { buildAuthHeaders } from "../forum/authHeaders";
import { findFeatureForPath, normalizeFeatures } from "./registry";

const FeatureRegistryContext = createContext(null);

async function requestRegistry(path, { method = "GET", body, signal, authenticated = true } = {}) {
  const headers = {
    Accept: "application/json",
    ...(body ? { "Content-Type": "application/json" } : {}),
  };
  const response = await fetch(path, {
    method,
    signal,
    // Preserve same-origin HTTP gateway credentials. The public API ignores
    // application sessions, including expired cookies, on the server.
    credentials: "same-origin",
    headers: authenticated ? buildAuthHeaders(headers) : headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.detail || `Registre indisponible (${response.status})`);
  return payload;
}

export const loadPublicFeatureRegistry = (signal) => requestRegistry("/api/features/", { signal, authenticated: false });
export const loadAdminFeatureRegistry = (signal) => requestRegistry("/api/features/admin/", { signal });
export const updateAdminFeature = (featureKey, body) => requestRegistry(`/api/features/admin/${encodeURIComponent(featureKey)}/`, { method: "PATCH", body });

export function FeatureRegistryProvider({ children }) {
  useSourceLanguage();
  const [features, setFeatures] = useState([]);
  const [source, setSource] = useState("loading");
  const [error, setError] = useState("");
  const [requiredDisclosures, setRequiredDisclosures] = useState({});

  const refresh = useCallback(async (signal) => {
    try {
      const payload = await loadPublicFeatureRegistry(signal);
      if (payload?.schemaVersion !== 2 || !Array.isArray(payload?.statusVocabulary)) {
        throw new Error("Version du registre produit incompatible.");
      }
      const next = normalizeFeatures(payload?.features);
      setFeatures(next);
      setRequiredDisclosures(payload?.requiredDisclosures || {});
      setSource("server");
      setError("");
      return next;
    } catch (error) {
      if (error?.name !== "AbortError") {
        setFeatures([]);
        setRequiredDisclosures({});
        setSource("unavailable");
        setError(error?.message || "Registre produit indisponible.");
      }
      return [];
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const value = useMemo(() => ({
    features,
    source,
    error,
    requiredDisclosures,
    ready: source === "server",
    today: features.filter((feature) => feature.section === "today"),
    vision: features.filter((feature) => feature.section === "vision"),
    getFeature: (featureKey) => features.find((feature) => feature.featureKey === featureKey) || null,
    getFeatureForPath: (pathname) => findFeatureForPath(features, pathname),
    refresh,
  }), [error, features, refresh, requiredDisclosures, source]);

  return <FeatureRegistryContext.Provider value={value}>{children}</FeatureRegistryContext.Provider>;
}

export function useFeatureRegistry() {
  const context = useContext(FeatureRegistryContext);
  if (!context) throw new Error("useFeatureRegistry doit être utilisé dans FeatureRegistryProvider");
  return context;
}

export function useOptionalFeatureRegistry() {
  return useContext(FeatureRegistryContext);
}
